import React, { useCallback, useMemo } from "react";
import range from "lodash/range";
import cn from "clsx";
import useT from "../../hooks/useT";
import Button from "../Button";
import { PaginationProps } from "../Pagination";
import "../Pagination/Pagination.css";
import "./PaginationDropDown.css";

export interface PaginationDropDownProps
  extends Pick<
    PaginationProps,
    Exclude<keyof PaginationProps, "onClickPageFn">
  > {
  onClickPageFn: React.ChangeEventHandler;
}

export function PaginationDropDown({
  currentPage = 1,
  maxPages = 1,
  className,
  onClickBack,
  onClickNext,
  onClickPageFn
}: PaginationDropDownProps) {
  const { t } = useT();

  // Event handlers
  const handleClickBack = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (onClickBack) {
        onClickBack(event);
      }
    },
    [onClickBack]
  );

  const handleClickNext = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (onClickNext) {
        onClickNext(event);
      }
    },
    [onClickNext]
  );

  const handleSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      onClickPageFn(event);
    },
    [onClickPageFn]
  );

  const options = useMemo(() => {
    return range(1, maxPages + 1).map(num => ({
      label: t("pagination.buttons.viewPage.label", { num }),
      value: num
    }));
  }, [maxPages]);

  return (
    <div className={cn("pagination", className)}>
      <ul>
        {!!onClickBack && (
          <li role="presentation" className="pagination-page-back">
            <Button
              className="button-role-pagination"
              theme="light"
              shape="solid"
              icon="arrow-left"
              label={t("pagination.buttons.prevPage.label")}
              ariaLabel={t("pagination.buttons.prevPage.ariaLabel")}
              disabled={currentPage === 1}
              onClick={handleClickBack}
            />
          </li>
        )}
        <li className="pagination-drop-down">
          <label className="button button-size-m button-theme-light button-shape-solid button-role-pagination">
            <span className="pagination-current">
              {currentPage} / {maxPages}
            </span>
            <select
              id="pagination-drop-down"
              className="pagination-drop-down"
              value={currentPage}
              onChange={handleSelect}
            >
              {options.map(option => {
                return (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </select>
          </label>
        </li>
        {!!onClickNext && (
          <li role="presentation" className="pagination-page-next">
            <Button
              className="button-role-pagination"
              theme="light"
              shape="solid"
              icon="arrow-right"
              label={t("pagination.buttons.nextPage.label")}
              ariaLabel={t("pagination.buttons.nextPage.ariaLabel")}
              disabled={currentPage === maxPages}
              onClick={handleClickNext}
            />
          </li>
        )}
      </ul>
    </div>
  );
}

export default PaginationDropDown;
